/**
 * SoundManager — Programmatic sound effects using Web Audio API.
 * Zero bundle size (no audio files). Each sound is synthesized on the fly.
 * Singleton pattern with localStorage persistence for mute state.
 */

const STORAGE_KEY_ENABLED = 'sound_effects_enabled';
const STORAGE_KEY_VOLUME = 'sound_effects_volume';

/** 🎭 Scene beds master switch (SCENE_SYSTEM_SPEC_2026_08_07 §4.1).
    Nina's 3-turn bed is explicitly a feel experiment ("let's see how it
    feels") — flip this to false to kill every scene bed everywhere without
    touching the scene one-shots, which are not in question. */
const SCENE_BEDS_ENABLED = true;

/** Hard ceiling on a scene bed, independent of anything the caller does.
    A scene nobody reacts to would otherwise hold its bed for the full
    DIRECTIVE_MAX_AGE_MS (10 min) that bounds the directive itself. The
    ceiling lives here, in the manager, so no caller can forget it. */
const SCENE_BED_MAX_MS = 180 * 1000;

class SoundManager {
  constructor() {
    this._ctx = null;
    this._enabled = this._loadBool(STORAGE_KEY_ENABLED, true);
    this._volume = this._loadFloat(STORAGE_KEY_VOLUME, 0.45);
    this._lastPlayGlobal = 0;
    this._lastPlayByType = {};
  }

  // --- Public API ---

  isEnabled() { return this._enabled; }
  getVolume() { return this._volume; }

  setEnabled(val) {
    this._enabled = !!val;
    try { localStorage.setItem(STORAGE_KEY_ENABLED, val ? '1' : '0'); } catch {}
    // 🎭 A scene bed is the one loop that can be running with nobody watching
    // for it, so muting has to kill it HERE rather than relying on every
    // caller to notice. (Ambient beds are left alone: their owners already
    // manage them, and changing that is not this arc's business.)
    if (!this._enabled) { try { this.stopSceneBed(); } catch (e) { /* non-critical */ } }
  }

  setVolume(val) {
    this._volume = Math.max(0, Math.min(1, val));
    try { localStorage.setItem(STORAGE_KEY_VOLUME, String(this._volume)); } catch {}
  }

  /** Resume the AudioContext inside a user gesture WITHOUT playing anything.
      For gestures whose sound is deferred (curtain tap-to-enter: the door creak
      moved to the Investigate click, but the tap must still autoplay-unlock the
      session so the cold open's thunder is legal seconds later). */
  unlock() {
    try {
      const ctx = this._getContext();
      if (ctx && ctx.state === 'suspended') ctx.resume();
    } catch (err) {
      // Silently fail — sounds are non-critical
    }
  }

  /** Play a named sound effect. Rate-limited per type and globally. */
  play(type) {
    if (!this._enabled || this._volume <= 0) return;
    const now = performance.now();

    // Global rate limit: 150ms between any sounds
    if (now - this._lastPlayGlobal < 150) return;
    // Per-type rate limit: 500ms
    if (this._lastPlayByType[type] && now - this._lastPlayByType[type] < 500) return;

    const fn = SOUNDS[type];
    if (!fn) return;

    try {
      const ctx = this._getContext();
      if (!ctx || ctx.state === 'closed') return;
      // Resume if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') ctx.resume();
      fn(ctx, this._volume);
      this._lastPlayGlobal = now;
      this._lastPlayByType[type] = now;
    } catch (err) {
      // Silently fail — sounds are non-critical
    }
  }

  /** 🎐 Ambient beds (teamlab ambience layer 1, 07-16 — SPACES_NEXT_LAYER
      spec Arc 1). One bed at a time, loop-managed outside play()'s one-shot
      flow: startAmbient(theme) is idempotent per theme (switching themes
      cross-fades via stop+start), stopAmbient() fades out over ~1.2s. All
      beds are VERY quiet by design — atmosphere, not soundtrack. Sparse
      event layers (clinks, chirps, bleeps) ride a timer the bed owns. */
  startAmbient(theme) {
    if (!this._enabled || this._volume <= 0) return;
    if (this._ambient) {
      if (this._ambient.theme === theme) return;
      this.stopAmbient();
    }
    const build = AMBIENT_BEDS[theme];
    if (!build) return;
    try {
      const ctx = this._getContext();
      if (!ctx || ctx.state === 'closed') return;
      if (ctx.state === 'suspended') {
        ctx.resume();
        // Reload trap (Nina 07-29 "rain never comes back"): after a full page
        // reload the resume() above is denied pre-gesture, the bed builds on a
        // suspended context and runs SILENT — and idempotency blocks retries.
        // Re-arm on the first real gesture so the already-built bed becomes
        // audible without needing another startAmbient call.
        if (!this._resumeArmed) {
          this._resumeArmed = true;
          const arm = () => {
            this._resumeArmed = false;
            try {
              const c = this._getContext();
              if (c && c.state === 'suspended') c.resume();
            } catch (e) { /* non-critical */ }
          };
          document.addEventListener('pointerdown', arm, { once: true, capture: true });
          document.addEventListener('keydown', arm, { once: true, capture: true });
        }
      }
      const bed = build(ctx, this._volume);
      this._ambient = { theme, ...bed };
    } catch (err) { /* non-critical */ }
  }

  stopAmbient() {
    const a = this._ambient;
    if (!a) return;
    this._ambient = null;
    try {
      if (a.timer) clearInterval(a.timer);
      const ctx = this._getContext();
      const t = ctx.currentTime;
      a.gain.gain.cancelScheduledValues(t);
      a.gain.gain.setValueAtTime(a.gain.gain.value, t);
      a.gain.gain.linearRampToValueAtTime(0.0001, t + 1.2);
      (a.sources || []).forEach((s) => { try { s.stop(t + 1.3); } catch (e) { /* already stopped */ } });
    } catch (err) { /* already gone */ }
  }

  /** 🎙️ Host-voice ducking (Arc 9, 07-20): pull the ambient bed down while
      the host speaks, restore after. Duck level is remembered on the bed so
      unduck returns to whatever the bed was actually at; both are no-ops
      when no bed is running. */
  duckAmbient() {
    const a = this._ambient;
    if (!a || !a.gain) return;
    try {
      const ctx = this._getContext();
      const t = ctx.currentTime;
      if (a._preDuck == null) a._preDuck = a.gain.gain.value;
      a.gain.gain.cancelScheduledValues(t);
      a.gain.gain.setValueAtTime(a.gain.gain.value, t);
      a.gain.gain.linearRampToValueAtTime(Math.max(0.0001, a._preDuck * 0.25), t + 0.3);
      // Failsafe (Nina 07-29): a stalled narration whose onended never fires
      // left the bed at 25% forever — reads as "the rain dropped out". No
      // narration runs 30s; auto-restore.
      if (a._duckTimer) clearTimeout(a._duckTimer);
      a._duckTimer = setTimeout(() => { try { this.unduckAmbient(); } catch (e) { /* gone */ } }, 30000);
    } catch (err) { /* non-critical */ }
  }

  unduckAmbient() {
    const a = this._ambient;
    if (!a) return;
    if (a._duckTimer) { clearTimeout(a._duckTimer); a._duckTimer = null; }
    if (!a.gain || a._preDuck == null) return;
    try {
      const ctx = this._getContext();
      const t = ctx.currentTime;
      a.gain.gain.cancelScheduledValues(t);
      a.gain.gain.setValueAtTime(a.gain.gain.value, t);
      a.gain.gain.linearRampToValueAtTime(a._preDuck, t + 0.6);
      a._preDuck = null;
    } catch (err) { /* non-critical */ }
  }

  /** 🌧️ Whodunnit rain — the original 07-13 bed, now one theme of many.
      stopRain only stops RAIN: the whodunnit else-branch must never kill a
      non-comp space's café/forest bed. */
  startRain() { this.startAmbient('rain'); }
  stopRain() { if (this._ambient && this._ambient.theme === 'rain') this.stopAmbient(); }

  /** ⛈️ Whodunnit interstitial storm (Nina 07-30) — door → loading → cold
      open, every pack. stopStorm mirrors stopRain's contract: only stops the
      storm we started, never a pack or non-comp bed. */
  startStorm() { this.startAmbient('storm'); }
  stopStorm() { if (this._ambient && this._ambient.theme === 'storm') this.stopAmbient(); }

  /** 🔎 Case ambience (whodunnit scenario packs, Nina 07-25): the pack names its
      bed via scenario.ambient. 🌦️ Weather gate (Nina 07-29): an ABSENT bed now
      means the case plays DRY — callers pass 'rain' only when the scene fiction
      has weather (the old unconditional fallback rained on every pack). Unknown
      non-null names (older payloads) still map to rain. stopCase mirrors
      stopRain's contract — it only stops the bed WE started, never a non-comp
      space's café/forest bed. */
  startCase(theme) {
    if (!theme) return;
    const bed = AMBIENT_BEDS[theme] ? theme : 'rain';
    this._caseTheme = bed;
    this.startAmbient(bed);
  }
  stopCase() {
    if (this._ambient && this._caseTheme && this._ambient.theme === this._caseTheme) this.stopAmbient();
    this._caseTheme = null;
  }

  /** 🎭 Scene beds (SCENE_SYSTEM_SPEC §4.1) — the short textural loop a /scene
      runs while it opens. Deliberately its OWN slot, not `_ambient`:
        - a scene must never stop a whodunnit case/storm bed, and stopAmbient
          must never stop a scene (the startRain/stopRain contract, generalized)
        - the two can legitimately overlap; they are different layers
      Lifetime is owned here, not by the caller. The bed dies at the FIRST of:
      3 room-turn consumptions (caller calls stopSceneBed), SCENE_BED_MAX_MS,
      scene expiry, tab hidden, space master mute, or SCENE_BEDS_ENABLED=false.
      Fades in 1.5s (inside each builder) and out 2.5s. */
  startSceneBed(key) {
    if (!SCENE_BEDS_ENABLED) return;
    if (!this._enabled || this._volume <= 0) return;
    if (this._sceneBed) {
      if (this._sceneBed.key === key) return; // idempotent per key
      this.stopSceneBed();
    }
    const build = SCENE_BEDS[key];
    if (!build) return;
    try {
      const ctx = this._getContext();
      if (!ctx || ctx.state === 'closed') return;
      if (ctx.state === 'suspended') {
        ctx.resume();
        // Same reload trap the ambient beds hit (Nina 07-29 "rain never comes
        // back"): pre-gesture the resume is denied, the bed builds silent, and
        // idempotency blocks a retry. Re-arm on the first real gesture.
        if (!this._resumeArmed) {
          this._resumeArmed = true;
          const arm = () => {
            this._resumeArmed = false;
            try {
              const c = this._getContext();
              if (c && c.state === 'suspended') c.resume();
            } catch (e) { /* non-critical */ }
          };
          document.addEventListener('pointerdown', arm, { once: true, capture: true });
          document.addEventListener('keydown', arm, { once: true, capture: true });
        }
      }
      const bed = build(ctx, this._volume);
      this._sceneBed = { key, ...bed };
      this._sceneBedCeiling = setTimeout(() => {
        try { this.stopSceneBed(); } catch (e) { /* gone */ }
      }, SCENE_BED_MAX_MS);
    } catch (err) { /* non-critical */ }
  }

  stopSceneBed() {
    if (this._sceneBedCeiling) { clearTimeout(this._sceneBedCeiling); this._sceneBedCeiling = null; }
    const b = this._sceneBed;
    if (!b) return;
    this._sceneBed = null;
    try {
      if (b.timer) clearInterval(b.timer);
      const ctx = this._getContext();
      const t = ctx.currentTime;
      b.gain.gain.cancelScheduledValues(t);
      b.gain.gain.setValueAtTime(b.gain.gain.value, t);
      b.gain.gain.linearRampToValueAtTime(0.0001, t + 2.5);
      (b.sources || []).forEach((s) => { try { s.stop(t + 2.6); } catch (e) { /* already stopped */ } });
    } catch (err) { /* already gone */ }
  }

  /** True while a scene bed is running — lets a caller avoid restarting one. */
  isSceneBedPlaying(key) {
    return !!(this._sceneBed && (!key || this._sceneBed.key === key));
  }

  // --- Internals ---

  _getContext() {
    if (!this._ctx || this._ctx.state === 'closed') {
      try { this._ctx = new (window.AudioContext || window.webkitAudioContext)(); }
      catch { return null; }
    }
    return this._ctx;
  }

  _loadBool(key, fallback) {
    try { const v = localStorage.getItem(key); return v === null ? fallback : v === '1'; }
    catch { return fallback; }
  }

  _loadFloat(key, fallback) {
    try { const v = localStorage.getItem(key); return v === null ? fallback : parseFloat(v) || fallback; }
    catch { return fallback; }
  }
}

// --- Sound Definitions ---
// Each function receives (AudioContext, volume) and plays a short sound.

function makeGain(ctx, vol) {
  const g = ctx.createGain();
  g.connect(ctx.destination);
  g.gain.value = vol;
  return g;
}

const SOUNDS = {
  /** Soft pop for new AI messages */
  message(ctx, vol) {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = makeGain(ctx, vol * 0.35);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, t);
    osc.frequency.exponentialRampToValueAtTime(400, t + 0.08);
    gain.gain.setValueAtTime(vol * 0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    osc.connect(gain);
    osc.start(t);
    osc.stop(t + 0.1);
  },

  /** Gentle filtered noise for private/secret messages */
  whisper(ctx, vol) {
    const t = ctx.currentTime;
    const bufferSize = ctx.sampleRate * 0.06;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2000;
    filter.Q.value = 2;
    const gain = makeGain(ctx, vol * 0.2);
    gain.gain.setValueAtTime(vol * 0.2, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    source.connect(filter);
    filter.connect(gain);
    source.start(t);
  },

  /** Quick confirmation blip when user sends a message */
  sent(ctx, vol) {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = makeGain(ctx, vol * 0.2);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, t);
    osc.frequency.exponentialRampToValueAtTime(1000, t + 0.04);
    gain.gain.setValueAtTime(vol * 0.2, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
    osc.connect(gain);
    osc.start(t);
    osc.stop(t + 0.06);
  },

  /** Warm two-tone chime for moderator messages */
  moderator(ctx, vol) {
    const t = ctx.currentTime;
    [523, 659].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = makeGain(ctx, vol * 0.25);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const offset = i * 0.06;
      gain.gain.setValueAtTime(0, t + offset);
      gain.gain.linearRampToValueAtTime(vol * 0.25, t + offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + offset + 0.15);
      osc.connect(gain);
      osc.start(t + offset);
      osc.stop(t + offset + 0.15);
    });
  },

  /** Rising 3-tone chord for game announcements */
  announcement(ctx, vol) {
    const t = ctx.currentTime;
    [440, 554, 659].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = makeGain(ctx, vol * 0.25);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const offset = i * 0.08;
      gain.gain.setValueAtTime(0, t + offset);
      gain.gain.linearRampToValueAtTime(vol * 0.25, t + offset + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, t + offset + 0.35);
      osc.connect(gain);
      osc.start(t + offset);
      osc.stop(t + offset + 0.35);
    });
  },

  /** Attention double-ping for your_turn */
  yourTurn(ctx, vol) {
    const t = ctx.currentTime;
    [0, 0.12].forEach((offset) => {
      const osc = ctx.createOscillator();
      const gain = makeGain(ctx, vol * 0.3);
      osc.type = 'triangle';
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(vol * 0.3, t + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, t + offset + 0.08);
      osc.connect(gain);
      osc.start(t + offset);
      osc.stop(t + offset + 0.08);
    });
  },

  /** Dramatic low tone for voting */
  vote(ctx, vol) {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = makeGain(ctx, vol * 0.3);
    osc.type = 'square';
    osc.frequency.setValueAtTime(220, t);
    osc.frequency.exponentialRampToValueAtTime(110, t + 0.3);
    gain.gain.setValueAtTime(vol * 0.3, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    osc.connect(gain);
    osc.start(t);
    osc.stop(t + 0.35);
  },

  /** Friendly ascending 2-note for player join */
  playerJoin(ctx, vol) {
    const t = ctx.currentTime;
    [440, 660].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = makeGain(ctx, vol * 0.2);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const offset = i * 0.07;
      gain.gain.setValueAtTime(vol * 0.2, t + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, t + offset + 0.1);
      osc.connect(gain);
      osc.start(t + offset);
      osc.stop(t + offset + 0.1);
    });
  },

  /** Descending 2-note for player leave */
  playerLeave(ctx, vol) {
    const t = ctx.currentTime;
    [660, 440].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = makeGain(ctx, vol * 0.2);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const offset = i * 0.07;
      gain.gain.setValueAtTime(vol * 0.2, t + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, t + offset + 0.1);
      osc.connect(gain);
      osc.start(t + offset);
      osc.stop(t + offset + 0.1);
    });
  },

  /** Deep thud for elimination */
  eliminated(ctx, vol) {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = makeGain(ctx, vol * 0.35);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(110, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.2);
    gain.gain.setValueAtTime(vol * 0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    osc.connect(gain);
    osc.start(t);
    osc.stop(t + 0.25);
  },

  /** Tiny sparkle for reactions */
  reaction(ctx, vol) {
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = makeGain(ctx, vol * 0.15);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(1200, t);
    osc.frequency.exponentialRampToValueAtTime(1800, t + 0.04);
    gain.gain.setValueAtTime(vol * 0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    osc.connect(gain);
    osc.start(t);
    osc.stop(t + 0.05);
  },

  /** Dice: NATURAL 20 — triumphant rising arpeggio (C5 E5 G5 C6), last note rings */
  diceCrit(ctx, vol) {
    const t = ctx.currentTime;
    [523, 659, 784, 1047].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = makeGain(ctx, vol * 0.22);
      osc.type = 'triangle';
      osc.frequency.value = freq;
      const off = i * 0.07;
      const len = i === 3 ? 0.5 : 0.16;
      gain.gain.setValueAtTime(0, t + off);
      gain.gain.linearRampToValueAtTime(vol * 0.22, t + off + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + off + len);
      osc.connect(gain);
      osc.start(t + off);
      osc.stop(t + off + len);
    });
  },

  /** Dice: solid roll — two confident ascending notes */
  diceSuccess(ctx, vol) {
    const t = ctx.currentTime;
    [587, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = makeGain(ctx, vol * 0.2);
      osc.type = 'triangle';
      osc.frequency.value = freq;
      const off = i * 0.07;
      gain.gain.setValueAtTime(vol * 0.2, t + off);
      gain.gain.exponentialRampToValueAtTime(0.001, t + off + 0.12);
      osc.connect(gain);
      osc.start(t + off);
      osc.stop(t + off + 0.12);
    });
  },

  // (diceComplication / diceFumble womp sounds removed 07-08 — user found them grating;
  // bad rolls are visual-only beats now.)

  /** Creaky door — slow irregular pitch groan + latch thunk (entry curtain "Enter" beat).
      Doubles as the session's audio-unlock gesture sound: it plays ON the click that
      resumes the AudioContext, so every later SFX (thunder, dice) is autoplay-legal. */
  doorCreak(ctx, vol) {
    const t = ctx.currentTime;
    // The groan: a low sawtooth crawling upward in hesitant steps, like hinges catching.
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(72, t);
    osc.frequency.linearRampToValueAtTime(96, t + 0.28);
    osc.frequency.setValueAtTime(88, t + 0.34);
    osc.frequency.linearRampToValueAtTime(132, t + 0.72);
    osc.frequency.setValueAtTime(120, t + 0.78);
    osc.frequency.linearRampToValueAtTime(168, t + 1.05);
    // Wobble the pitch slightly — the "catching" texture that reads as wood, not synth.
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.type = 'sine';
    lfo.frequency.value = 13;
    lfoGain.gain.value = 9;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(340, t);
    filter.frequency.linearRampToValueAtTime(620, t + 1.05);
    filter.Q.value = 5;
    const gain = makeGain(ctx, vol * 0.28);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(vol * 0.28, t + 0.06);
    gain.gain.setValueAtTime(vol * 0.22, t + 0.5);
    gain.gain.linearRampToValueAtTime(vol * 0.3, t + 0.8);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 1.15);
    osc.connect(filter);
    filter.connect(gain);
    osc.start(t);
    osc.stop(t + 1.15);
    lfo.start(t);
    lfo.stop(t + 1.15);
    // The latch: a soft low thunk as the door settles open.
    const thunk = ctx.createOscillator();
    const tg = makeGain(ctx, vol * 0.3);
    thunk.type = 'sine';
    thunk.frequency.setValueAtTime(140, t + 1.02);
    thunk.frequency.exponentialRampToValueAtTime(58, t + 1.18);
    tg.gain.setValueAtTime(0, t + 1.02);
    tg.gain.linearRampToValueAtTime(vol * 0.3, t + 1.04);
    tg.gain.exponentialRampToValueAtTime(0.001, t + 1.3);
    thunk.connect(tg);
    thunk.start(t + 1.02);
    thunk.stop(t + 1.3);
  },

  /** Airy rising shimmer for stepping into a non-competitive space — soft major
      arpeggio over a breathy swell. Replaces the door creak there (user call
      07-10: the creak reads as horror; entering a world should read as wonder). */
  enterSpace(ctx, vol) {
    const t = ctx.currentTime;
    // Breathy swell underneath — air moving as the doors open
    const bufferSize = Math.floor(ctx.sampleRate * 0.9);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.4;
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const nf = ctx.createBiquadFilter();
    nf.type = 'bandpass';
    nf.frequency.setValueAtTime(900, t);
    nf.frequency.exponentialRampToValueAtTime(2600, t + 0.7);
    nf.Q.value = 1.2;
    const ng = makeGain(ctx, vol * 0.12);
    ng.gain.setValueAtTime(0.0001, t);
    ng.gain.linearRampToValueAtTime(vol * 0.12, t + 0.35);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.9);
    noise.connect(nf);
    nf.connect(ng);
    noise.start(t);
    // Rising arpeggio — C5 E5 G5 C6, each note blooming softly into the next
    [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      const t0 = t + i * 0.11;
      const osc = ctx.createOscillator();
      const g = makeGain(ctx, vol * 0.16);
      osc.type = 'sine';
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.linearRampToValueAtTime(vol * (0.16 - i * 0.015), t0 + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.75);
      osc.connect(g);
      osc.start(t0);
      osc.stop(t0 + 0.8);
    });
  },

  /** Lamp flicking on — switch click + fluorescent buzz gated to the flicker rhythm.
      Whodunnit file-pull beat's swinging interrogation lamp; the gain steps mirror
      the CSS lampFlickerOn keyframes (2.4s), so light and sound stutter together. */
  lampBuzz(ctx, vol) {
    const t = ctx.currentTime;
    // The switch: a tiny high-passed noise tick.
    const clickDur = 0.03;
    const cbuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * clickDur), ctx.sampleRate);
    const cd = cbuf.getChannelData(0);
    for (let i = 0; i < cd.length; i++) cd[i] = (Math.random() * 2 - 1) * (1 - i / cd.length);
    const click = ctx.createBufferSource();
    click.buffer = cbuf;
    const chp = ctx.createBiquadFilter();
    chp.type = 'highpass';
    chp.frequency.value = 1800;
    const cg = makeGain(ctx, vol * 0.5);
    cg.gain.setValueAtTime(vol * 0.5, t);
    cg.gain.exponentialRampToValueAtTime(0.001, t + clickDur);
    click.connect(chp);
    chp.connect(cg);
    click.start(t);
    // The buzz: a mains-hum sawtooth pair, amplitude-gated to the flicker.
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.value = 118;
    const osc2 = ctx.createOscillator();
    osc2.type = 'square';
    osc2.frequency.value = 236;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 900;
    filter.Q.value = 1.6;
    const g = makeGain(ctx, 0);
    const on = vol * 0.14, dim = vol * 0.02;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(on, t + 0.19);          // first strike
    g.gain.setValueAtTime(dim, t + 0.29);                  // cuts out
    g.gain.linearRampToValueAtTime(on * 0.85, t + 0.43);   // catches again
    g.gain.setValueAtTime(dim, t + 0.58);                  // cuts out
    g.gain.linearRampToValueAtTime(on, t + 0.82);          // holds
    g.gain.setValueAtTime(on, t + 1.7);
    g.gain.exponentialRampToValueAtTime(0.001, t + 2.6);   // settles to room tone
    osc.connect(filter);
    osc2.connect(filter);
    filter.connect(g);
    osc.start(t + 0.12);
    osc.stop(t + 2.6);
    osc2.start(t + 0.12);
    osc2.stop(t + 2.6);
  },

  /** ✨ Sparkle — the discovery "ooh!" (user ask 07-15): rising three-note
      shimmer + high tail for fresh cut-scene cards (first discoveries,
      first ships). Bright but small — a gasp, not a fanfare. */
  sparkle(ctx, vol) {
    const t = ctx.currentTime;
    [0, 0.09, 0.18].forEach((d, i) => {
      const o = ctx.createOscillator();
      o.type = 'sine';
      const f = 880 * Math.pow(1.26, i);
      o.frequency.setValueAtTime(f, t + d);
      o.frequency.exponentialRampToValueAtTime(f * 1.25, t + d + 0.09);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t + d);
      g.gain.exponentialRampToValueAtTime(vol * 0.35, t + d + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.1);
      o.connect(g); g.connect(ctx.destination);
      o.start(t + d); o.stop(t + d + 0.15);
    });
    const tail = ctx.createOscillator();
    tail.type = 'sine';
    tail.frequency.setValueAtTime(2100, t + 0.28);
    tail.frequency.exponentialRampToValueAtTime(2650, t + 0.5);
    const tg = ctx.createGain();
    tg.gain.setValueAtTime(0.0001, t + 0.28);
    tg.gain.exponentialRampToValueAtTime(vol * 0.2, t + 0.31);
    tg.gain.exponentialRampToValueAtTime(0.0001, t + 0.52);
    tail.connect(tg); tg.connect(ctx.destination);
    tail.start(t + 0.28); tail.stop(t + 0.55);
  },

  /* ── 🔍 Whodunnit Stage-1 diegetic clue-sounds (immersion batch item 7,
        08-12). The collect-clues stage's interaction layer: mini-game touch
        textures + the filing shutter. All deliberately small and dry — the
        pack ambience bed carries the room; these are your HANDS. ── */

  /** 📸 Camera shutter — the evidence-filing beat (fires with the white flash) */
  cameraShutter(ctx, vol) {
    const t = ctx.currentTime;
    [[0, 5200, 0.5], [0.07, 2600, 0.8]].forEach(([d, f, amp]) => {
      const len = Math.floor(ctx.sampleRate * 0.035);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass'; bp.frequency.value = f; bp.Q.value = 1.2;
      const g = makeGain(ctx, vol * 0.4 * amp);
      g.gain.setValueAtTime(vol * 0.4 * amp, t + d);
      g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.055);
      src.connect(bp); bp.connect(g);
      src.start(t + d);
    });
  },

  /** 🫳 Brush rub — dusting powder / the polaroid's paper whoosh (throttled by caller) */
  brushRub(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 0.14;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const env = Math.sin((i / data.length) * Math.PI);
      data[i] = (Math.random() * 2 - 1) * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass'; lp.frequency.value = 1400 + Math.random() * 600;
    const g = makeGain(ctx, vol * 0.16);
    src.connect(lp); lp.connect(g);
    src.start(t);
  },

  /** 🔦 UV ping — a hidden trace catches the black light */
  uvPing(ctx, vol) {
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(1150, t);
    o.frequency.exponentialRampToValueAtTime(1420, t + 0.12);
    const g = makeGain(ctx, vol * 0.22);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol * 0.22, t + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    o.connect(g);
    o.start(t); o.stop(t + 0.25);
  },

  /** ⚪ Chalk tick — one outline dot set */
  chalkTick(ctx, vol) {
    const t = ctx.currentTime;
    const len = Math.floor(ctx.sampleRate * 0.02);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.value = 3400 + Math.random() * 900; bp.Q.value = 2;
    const g = makeGain(ctx, vol * 0.18);
    src.connect(bp); bp.connect(g);
    src.start(t);
  },

  /** 🌊 Water plop — one still-water ripple ring */
  waterPlop(ctx, vol) {
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sine';
    const f0 = 420 + Math.random() * 180;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f0 * 0.38, t + 0.13);
    const g = makeGain(ctx, vol * 0.22);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol * 0.22, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
    o.connect(g);
    o.start(t); o.stop(t + 0.26);
  },

  /** 🖋 Ink swish — a bloom spreading under the stroke (throttled by caller) */
  inkSwish(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 0.3;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      const env = Math.pow(Math.sin((i / data.length) * Math.PI), 2);
      data[i] = (Math.random() * 2 - 1) * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.value = 750 + Math.random() * 350; bp.Q.value = 1.4;
    const g = makeGain(ctx, vol * 0.12);
    src.connect(bp); bp.connect(g);
    src.start(t);
  },

  /** ✨ Firefly chime — one mote settles on the evidence */
  fireflyChime(ctx, vol) {
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sine';
    const f = [1567.98, 1760, 2093, 2349.32][Math.floor(Math.random() * 4)]; // G6 A6 C7 D7
    o.frequency.value = f;
    const g = makeGain(ctx, vol * 0.14);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol * 0.14, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    o.connect(g);
    o.start(t); o.stop(t + 0.55);
  },

  /** 🎷 Noir sting — a dark broken chord for round transitions (Whodunnit
      ambience, 07-13). Dm-ish arpeggio, low and unhurried: "the plot turns",
      not a fanfare. */
  noirSting(ctx, vol) {
    const t = ctx.currentTime;
    const notes = [146.83, 174.61, 220.0, 261.63]; // D3 F3 A3 C4
    notes.forEach((f, i) => {
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      const g = ctx.createGain();
      const start = t + i * 0.22;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(vol * 0.09, start + 0.04);
      g.gain.exponentialRampToValueAtTime(0.001, start + 1.6);
      o.connect(g); g.connect(ctx.destination);
      o.start(start); o.stop(start + 1.7);
    });
    // Low room swell under the chord
    const swell = ctx.createOscillator();
    swell.type = 'sine';
    swell.frequency.setValueAtTime(73.4, t); // D2
    const sg = ctx.createGain();
    sg.gain.setValueAtTime(0.0001, t);
    sg.gain.linearRampToValueAtTime(vol * 0.05, t + 0.6);
    sg.gain.linearRampToValueAtTime(0.0001, t + 2.2);
    swell.connect(sg); sg.connect(ctx.destination);
    swell.start(t); swell.stop(t + 2.3);
  },

  /** A small, distant shriek — the body-discovery beat (Whodunnit cold open).
      Deliberately quiet and short: a far-off scream down the hall, not a jump scare.
      Detuned saw pair sweeps up, hangs, and breaks downward through a vocal-band
      filter, with a whisper of breath noise underneath.
      v2 (07-13, "use a better scream"): softer voice (gentler vibrato, darker
      filter) + a low string swell underneath — reads as dread, not a smoke
      alarm. Tuning knobs: the two filter frequencies + the swell gain. */
  shriek(ctx, vol) {
    // Low string swell — dread under the voice
    const cello = ctx.createOscillator();
    cello.type = 'sawtooth';
    cello.frequency.setValueAtTime(110, ctx.currentTime);
    cello.frequency.linearRampToValueAtTime(82, ctx.currentTime + 0.9);
    const cf = ctx.createBiquadFilter();
    cf.type = 'lowpass'; cf.frequency.value = 300;
    const cg = ctx.createGain();
    cg.gain.setValueAtTime(0.0001, ctx.currentTime);
    cg.gain.linearRampToValueAtTime(vol * 0.06, ctx.currentTime + 0.25);
    cg.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 1.0);
    cello.connect(cf); cf.connect(cg); cg.connect(ctx.destination);
    cello.start(ctx.currentTime); cello.stop(ctx.currentTime + 1.05);
    SOUNDS._shriekVoice(ctx, vol); // plain call — play() invokes without `this`
  },

  _shriekVoice(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 0.55;
    // Breath/air under the voice
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      const env = Math.sin((i / d.length) * Math.PI);
      d[i] = (Math.random() * 2 - 1) * env * 0.4;
    }
    const air = ctx.createBufferSource();
    air.buffer = buf;
    const abp = ctx.createBiquadFilter();
    abp.type = 'bandpass';
    abp.frequency.value = 2400;
    abp.Q.value = 0.8;
    const ag = makeGain(ctx, vol * 0.05);
    air.connect(abp);
    abp.connect(ag);
    air.start(t);
    // The voice: two detuned saws, pitch rising then cracking downward
    [0, 9].forEach(detune => {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.detune.value = detune;
      osc.frequency.setValueAtTime(620, t);
      osc.frequency.exponentialRampToValueAtTime(1180, t + 0.16);
      osc.frequency.setValueAtTime(1180, t + 0.3);
      osc.frequency.exponentialRampToValueAtTime(760, t + dur);
      // Fast vibrato is what reads as a human voice rather than a siren
      const vib = ctx.createOscillator();
      vib.frequency.value = 26;
      const vg = ctx.createGain();
      vg.gain.value = 22; // v2: gentler vibrato (34 read as a siren)
      vib.connect(vg);
      vg.connect(osc.frequency);
      vib.start(t);
      vib.stop(t + dur);
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 900; // v2: darker voice band
      filter.Q.value = 1.4;
      const g = makeGain(ctx, vol * 0.11);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(vol * 0.11, t + 0.08);
      g.gain.setValueAtTime(vol * 0.11, t + 0.3);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      osc.connect(filter);
      filter.connect(g);
      osc.start(t);
      osc.stop(t + dur);
    });
  },

  /** Thunder — noise crack decaying into a sub rumble (Whodunnit cold open lightning) */
  thunder(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 1.8;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      const decay = Math.pow(1 - i / d.length, 2.2);
      d[i] = (Math.random() * 2 - 1) * decay;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(3200, t);
    filter.frequency.exponentialRampToValueAtTime(220, t + dur * 0.7);
    const gain = makeGain(ctx, vol * 0.5);
    gain.gain.setValueAtTime(vol * 0.5, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filter);
    filter.connect(gain);
    src.start(t);
    const rum = ctx.createOscillator();
    const rg = makeGain(ctx, vol * 0.3);
    rum.type = 'sine';
    rum.frequency.setValueAtTime(55, t);
    rum.frequency.exponentialRampToValueAtTime(38, t + 1.4);
    rg.gain.setValueAtTime(0, t);
    rg.gain.linearRampToValueAtTime(vol * 0.3, t + 0.08);
    rg.gain.exponentialRampToValueAtTime(0.001, t + 1.6);
    rum.connect(rg);
    rum.start(t);
    rum.stop(t + 1.6);
  },

  /** 🔨 Soft wood-knock — something new landed on the shelf */
  woodKnock(ctx, vol) {
    const t = ctx.currentTime;
    const thump = ctx.createOscillator();
    const tg = makeGain(ctx, vol * 0.3);
    thump.type = 'sine';
    thump.frequency.setValueAtTime(180, t);
    thump.frequency.exponentialRampToValueAtTime(90, t + 0.09);
    tg.gain.setValueAtTime(vol * 0.3, t);
    tg.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    thump.connect(tg);
    thump.start(t);
    thump.stop(t + 0.12);
    const bufferSize = ctx.sampleRate * 0.04;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * 0.6;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 900;
    filter.Q.value = 1.2;
    const ng = makeGain(ctx, vol * 0.12);
    ng.gain.setValueAtTime(vol * 0.12, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    src.connect(filter);
    filter.connect(ng);
    src.start(t);
  },

  /** 🗳️ Gentle two-note up-bell — a poll just opened */
  pollOpen(ctx, vol) {
    const t = ctx.currentTime;
    [660, 880].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = makeGain(ctx, vol * 0.18);
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, t + i * 0.09);
      gain.gain.linearRampToValueAtTime(vol * 0.18, t + i * 0.09 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + i * 0.09 + 0.3);
      osc.connect(gain);
      osc.start(t + i * 0.09);
      osc.stop(t + i * 0.09 + 0.3);
    });
  },

  // ── 🎭 SCENE FX one-shots (SCENE_SYSTEM_SPEC_2026_08_07 §4) ──────────────
  // Fired by a /scene card landing in the room (data.fx.kind), through the
  // same fireHemFx rail as the poll-enactment fx: master mute, rate limits and
  // autoplay-unlock all apply, replay is blocked by a module-level fired-once
  // Set plus a fresh-card age guard. Synthesized like everything above — no
  // assets, because public/ ships wholesale on every deploy.

  /** 🔥 Fire crackle — a handful of irregular resinous pops over a soft hiss.
      Irregularity is the whole sound: evenly spaced pops read as a machine. */
  fireCrackle(ctx, vol) {
    const t = ctx.currentTime;
    // Bed of hiss
    const hissDur = 0.55;
    const hbuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * hissDur), ctx.sampleRate);
    const hd = hbuf.getChannelData(0);
    for (let i = 0; i < hd.length; i++) {
      const env = Math.sin((i / hd.length) * Math.PI);
      hd[i] = (Math.random() * 2 - 1) * env;
    }
    const hsrc = ctx.createBufferSource();
    hsrc.buffer = hbuf;
    const hbp = ctx.createBiquadFilter();
    hbp.type = 'bandpass'; hbp.frequency.value = 1400; hbp.Q.value = 0.7;
    const hg = makeGain(ctx, vol * 0.09);
    hsrc.connect(hbp); hbp.connect(hg);
    hsrc.start(t);
    // 5-8 pops at random offsets, random brightness
    const pops = 5 + Math.floor(Math.random() * 4);
    for (let i = 0; i < pops; i++) {
      const at = t + Math.random() * 0.5;
      const len = 0.012 + Math.random() * 0.022;
      const pbuf = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * len)), ctx.sampleRate);
      const pd = pbuf.getChannelData(0);
      for (let j = 0; j < pd.length; j++) {
        pd[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / pd.length, 1.6);
      }
      const psrc = ctx.createBufferSource();
      psrc.buffer = pbuf;
      const pbp = ctx.createBiquadFilter();
      pbp.type = 'bandpass';
      pbp.frequency.value = 900 + Math.random() * 2600;
      pbp.Q.value = 1.2 + Math.random();
      const pg = makeGain(ctx, vol * (0.10 + Math.random() * 0.16));
      psrc.connect(pbp); pbp.connect(pg);
      psrc.start(at);
    }
  },

  /** 🔨 Glass break — bright burst then a scatter of falling shards */
  glassBreak(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 0.14;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.1);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 2200;
    const g = makeGain(ctx, vol * 0.3);
    src.connect(hp); hp.connect(g);
    src.start(t);
    for (let i = 0; i < 7; i++) {
      const at = t + 0.05 + Math.random() * 0.42;
      const osc = ctx.createOscillator();
      const sg = makeGain(ctx, vol * 0.07);
      osc.type = 'triangle';
      const f = 2600 + Math.random() * 3200;
      osc.frequency.setValueAtTime(f, at);
      osc.frequency.exponentialRampToValueAtTime(f * 0.7, at + 0.1);
      sg.gain.setValueAtTime(vol * 0.07, at);
      sg.gain.exponentialRampToValueAtTime(0.001, at + 0.12);
      osc.connect(sg);
      osc.start(at); osc.stop(at + 0.12);
    }
  },

  /** 🌬️ Wind gust — noise swelling through a sweeping bandpass */
  windGust(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 1.5;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.Q.value = 1.1;
    bp.frequency.setValueAtTime(320, t);
    bp.frequency.exponentialRampToValueAtTime(1250, t + dur * 0.45);
    bp.frequency.exponentialRampToValueAtTime(260, t + dur);
    const g = makeGain(ctx, 0.0001);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol * 0.2, t + dur * 0.4);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(bp); bp.connect(g);
    src.start(t); src.stop(t + dur);
  },

  /** 👣 Footsteps — four soft thuds, humanly uneven */
  footsteps(ctx, vol) {
    const t = ctx.currentTime;
    let at = t;
    for (let i = 0; i < 4; i++) {
      const len = 0.09;
      const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * len), ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / d.length, 2.4);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass'; lp.frequency.value = 260 + Math.random() * 90;
      const g = makeGain(ctx, vol * (0.16 + Math.random() * 0.06));
      src.connect(lp); lp.connect(g);
      src.start(at);
      at += 0.26 + Math.random() * 0.07;
    }
  },

  /** 🕰️ Clock chime — struck bell with inharmonic partials */
  clockChime(ctx, vol) {
    const t = ctx.currentTime;
    const partials = [1, 2.01, 2.98, 4.17, 5.43];
    const base = 523;
    partials.forEach((mult, i) => {
      const osc = ctx.createOscillator();
      const g = makeGain(ctx, 0.0001);
      osc.type = 'sine';
      osc.frequency.value = base * mult;
      const amp = vol * 0.16 / (i + 1);
      g.gain.setValueAtTime(amp, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 2.4 - i * 0.28);
      osc.connect(g);
      osc.start(t); osc.stop(t + 2.5);
    });
  },

  /** 🫀 Heartbeat — two lub-dubs, felt more than heard */
  heartbeat(ctx, vol) {
    const t = ctx.currentTime;
    [[0, 0.30], [0.30, 0.20], [0.85, 0.30], [1.15, 0.20]].forEach(([off, amp]) => {
      const osc = ctx.createOscillator();
      const g = makeGain(ctx, 0.0001);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(78, t + off);
      osc.frequency.exponentialRampToValueAtTime(42, t + off + 0.16);
      g.gain.setValueAtTime(0.0001, t + off);
      g.gain.linearRampToValueAtTime(vol * amp, t + off + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t + off + 0.2);
      osc.connect(g);
      osc.start(t + off); osc.stop(t + off + 0.22);
    });
  },

  /** 🔌 Power down — everything winding to a stop */
  powerDown(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 1.3;
    const osc = ctx.createOscillator();
    const g = makeGain(ctx, 0.0001);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(340, t);
    osc.frequency.exponentialRampToValueAtTime(34, t + dur);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(1800, t);
    lp.frequency.exponentialRampToValueAtTime(180, t + dur);
    g.gain.setValueAtTime(vol * 0.16, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(lp); lp.connect(g);
    osc.start(t); osc.stop(t + dur);
  },

  /** 💨 Whoosh — something passes through the room */
  whoosh(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 0.6;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      const env = Math.sin((i / d.length) * Math.PI);
      d[i] = (Math.random() * 2 - 1) * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.Q.value = 0.9;
    bp.frequency.setValueAtTime(280, t);
    bp.frequency.exponentialRampToValueAtTime(2400, t + dur * 0.55);
    bp.frequency.exponentialRampToValueAtTime(400, t + dur);
    const g = makeGain(ctx, vol * 0.22);
    src.connect(bp); bp.connect(g);
    src.start(t);
  },

  /** 💦 Splash — impact then droplets */
  splash(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 0.4;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.4);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(3600, t);
    lp.frequency.exponentialRampToValueAtTime(500, t + dur);
    const g = makeGain(ctx, vol * 0.26);
    src.connect(lp); lp.connect(g);
    src.start(t);
    for (let i = 0; i < 4; i++) {
      const at = t + 0.14 + Math.random() * 0.35;
      const osc = ctx.createOscillator();
      const dg = makeGain(ctx, vol * 0.05);
      osc.type = 'sine';
      const f = 1100 + Math.random() * 1400;
      osc.frequency.setValueAtTime(f * 0.6, at);
      osc.frequency.exponentialRampToValueAtTime(f, at + 0.05);
      dg.gain.setValueAtTime(vol * 0.05, at);
      dg.gain.exponentialRampToValueAtTime(0.001, at + 0.07);
      osc.connect(dg);
      osc.start(at); osc.stop(at + 0.08);
    }
  },

  /** ⚡ Zap — a short electrical bite */
  zap(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 0.22;
    const osc = ctx.createOscillator();
    const g = makeGain(ctx, 0.0001);
    osc.type = 'square';
    osc.frequency.setValueAtTime(2400, t);
    osc.frequency.exponentialRampToValueAtTime(120, t + dur);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.value = 1800; bp.Q.value = 2.4;
    g.gain.setValueAtTime(vol * 0.16, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(bp); bp.connect(g);
    osc.start(t); osc.stop(t + dur);
  },

  /** 🔔 Bell toll — deep, slow, and a long way off */
  bellToll(ctx, vol) {
    const t = ctx.currentTime;
    [1, 2.04, 3.11, 4.62].forEach((mult, i) => {
      const osc = ctx.createOscillator();
      const g = makeGain(ctx, 0.0001);
      osc.type = 'sine';
      osc.frequency.value = 174 * mult;
      const amp = vol * 0.2 / (i + 1.2);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(amp, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 3.4 - i * 0.45);
      osc.connect(g);
      osc.start(t); osc.stop(t + 3.5);
    });
  },

  /** 🗣️ Crowd murmur — a room of people, none of them intelligible */
  crowdMurmur(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 1.4;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      // Slow syllabic wobble so it reads as voices, not wind
      const wob = 0.55 + 0.45 * Math.sin((i / ctx.sampleRate) * Math.PI * 2 * 5.5);
      const env = Math.sin((i / d.length) * Math.PI);
      d[i] = (Math.random() * 2 - 1) * wob * env;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.frequency.value = 520; bp.Q.value = 0.8;
    const g = makeGain(ctx, vol * 0.16);
    src.connect(bp); bp.connect(g);
    src.start(t);
  },

  /** 📀 Record scratch — the needle comes off */
  recordScratch(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 0.42;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) {
      const rasp = Math.sin((i / ctx.sampleRate) * Math.PI * 2 * 62) > 0 ? 1 : 0.35;
      d[i] = (Math.random() * 2 - 1) * rasp * Math.pow(1 - i / d.length, 0.6);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass'; bp.Q.value = 3.2;
    bp.frequency.setValueAtTime(2200, t);
    bp.frequency.exponentialRampToValueAtTime(420, t + dur);
    const g = makeGain(ctx, vol * 0.2);
    src.connect(bp); bp.connect(g);
    src.start(t);
  },

  /** 🌋 Rumble — something large, somewhere below */
  rumble(ctx, vol) {
    const t = ctx.currentTime;
    const dur = 1.9;
    const src = makeLoopingNoise(ctx, 2);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass'; lp.frequency.value = 110; lp.Q.value = 0.7;
    const g = makeGain(ctx, 0.0001);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol * 0.34, t + 0.5);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(lp); lp.connect(g);
    src.start(t); src.stop(t + dur);
    const sub = ctx.createOscillator();
    const sg = makeGain(ctx, 0.0001);
    sub.type = 'sine';
    sub.frequency.setValueAtTime(46, t);
    sub.frequency.exponentialRampToValueAtTime(30, t + dur);
    sg.gain.setValueAtTime(0.0001, t);
    sg.gain.linearRampToValueAtTime(vol * 0.22, t + 0.4);
    sg.gain.exponentialRampToValueAtTime(0.001, t + dur);
    sub.connect(sg);
    sub.start(t); sub.stop(t + dur);
  },
};

// ── 🎐 Ambient beds (spec Arc 1) ────────────────────────────────────────────
// Each builder returns { gain, sources, timer? }: `gain` is the master node
// stopAmbient fades, `sources` get stop()ed after the fade, `timer` (sparse
// event scheduler) gets cleared immediately. Sparse events use their own
// short envelopes straight to destination — an in-flight clink after stop is
// sub-half-second and not worth plumbing. Everything synthesized: no assets.

function makeLoopingNoise(ctx, dur = 4) {
  const buf = ctx.createBuffer(1, ctx.sampleRate * dur, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  return src;
}

const AMBIENT_BEDS = {
  /** ⛈️ Interstitial storm (Nina 07-30): the whodunnit door → loading →
      cold-open bed, for EVERY pack. Same rain DNA mixed HOT — those screens
      are otherwise silent, so the bed IS the show there — plus a slow wind
      swell (LFO breathing the lowpass). Hands off to the pack's own quiet
      bed (or silence) when the room reveals — see GameStepPage's handoff. */
  storm(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 240;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 1100; lp.Q.value = 0.4;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.13, ctx.currentTime + 1.6);
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 420;
    lfo.connect(lfoGain); lfoGain.connect(lp.frequency); lfo.start();
    src.connect(hp); hp.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start();
    return { gain: g, sources: [src, lfo] };
  },

  /** 🌧️ Noir rain (the original whodunnit bed, verbatim) */
  rain(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 260;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 950; lp.Q.value = 0.4;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.045, ctx.currentTime + 2.5);
    src.connect(hp); hp.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start();
    return { gain: g, sources: [src] };
  },

  /** ☕ Café — low murmur + the occasional cup clink */
  cafe(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 420; lp.Q.value = 0.5;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.035, ctx.currentTime + 3);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start();
    const timer = setInterval(() => {
      if (Math.random() > 0.3) return; // sparse: a clink roughly every ~13s
      const t = ctx.currentTime;
      [1800, 2400].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const cg = makeGain(ctx, vol * 0.02);
        osc.type = 'sine';
        osc.frequency.value = freq + Math.random() * 120;
        cg.gain.setValueAtTime(vol * 0.02, t + i * 0.03);
        cg.gain.exponentialRampToValueAtTime(0.001, t + i * 0.03 + 0.09);
        osc.connect(cg);
        osc.start(t + i * 0.03);
        osc.stop(t + i * 0.03 + 0.09);
      });
    }, 4000);
    return { gain: g, sources: [src], timer };
  },

  /** 🌲 Forest — wind swell (LFO on the filter) + rare soft chirps */
  forest(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 520; lp.Q.value = 0.4;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.07;
    lfoGain.gain.value = 220;
    lfo.connect(lfoGain); lfoGain.connect(lp.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.04, ctx.currentTime + 3);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(); lfo.start();
    const timer = setInterval(() => {
      if (Math.random() > 0.25) return; // a chirp every ~20s
      const t = ctx.currentTime;
      [2600, 2100].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const cg = makeGain(ctx, vol * 0.013);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, t + i * 0.12);
        osc.frequency.exponentialRampToValueAtTime(freq * 0.82, t + i * 0.12 + 0.08);
        cg.gain.setValueAtTime(vol * 0.013, t + i * 0.12);
        cg.gain.exponentialRampToValueAtTime(0.001, t + i * 0.12 + 0.1);
        osc.connect(cg);
        osc.start(t + i * 0.12);
        osc.stop(t + i * 0.12 + 0.1);
      });
    }, 5000);
    return { gain: g, sources: [src, lfo], timer };
  },

  /** 🕹️ Arcade — faint room hum + very sparse quiet bleeps */
  arcade(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 300; lp.Q.value = 0.5;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.02, ctx.currentTime + 3);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start();
    const notes = [392, 523, 587, 659, 784];
    const timer = setInterval(() => {
      if (Math.random() > 0.3) return; // a bleep every ~13s
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const bg = makeGain(ctx, vol * 0.012);
      osc.type = 'square';
      osc.frequency.value = notes[Math.floor(Math.random() * notes.length)];
      bg.gain.setValueAtTime(vol * 0.012, t);
      bg.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
      osc.connect(bg);
      osc.start(t);
      osc.stop(t + 0.09);
    }, 4000);
    return { gain: g, sources: [src], timer };
  },

  /** 🫧 Airy — a barely-there detuned pad that slowly breathes. Nina 07-24:
      the old 220/220.9 pair beat at ~1Hz — a constant low BUZZ, and this was
      the default for every unmatched space. Detune slowed to a ~0.25Hz swell
      and the pad demoted to a keyword pick ('chimes' is the default now). */
  airy(ctx, vol) {
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.015, ctx.currentTime + 4);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 500;
    lp.connect(g); g.connect(ctx.destination);
    const sources = [];
    [220, 220.25, 330.2].forEach((freq) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(lp);
      osc.start();
      sources.push(osc);
    });
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.05;
    lfoGain.gain.value = vol * 0.005; // breathe ±a third of the bed level
    lfo.connect(lfoGain); lfoGain.connect(g.gain);
    lfo.start();
    sources.push(lfo);
    return { gain: g, sources };
  },

  /** 🎐 Chimes — THE default bed (Nina 07-24: "a truly nice sounding sound").
      Warm air (soft lowpassed noise, slow filter breathing) + sparse wind
      chimes: 1-3 pentatonic sine plinks with long decays and a whisper of
      detune shimmer, a cluster roughly every ~12-20s. No droning tones at
      all — between clusters it is just air. */
  chimes(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 360; lp.Q.value = 0.4;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.04;
    lfoGain.gain.value = 110;
    lfo.connect(lfoGain); lfoGain.connect(lp.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.022, ctx.currentTime + 3.5);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(); lfo.start();
    // C-major pentatonic, one octave up — bell register without shrillness
    const notes = [523.25, 587.33, 659.25, 783.99, 880.0];
    const timer = setInterval(() => {
      if (Math.random() > 0.4) return; // a cluster roughly every ~15s
      const t = ctx.currentTime;
      const count = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) {
        const freq = notes[Math.floor(Math.random() * notes.length)];
        const at = t + i * (0.25 + Math.random() * 0.3);
        const decay = 1.3 + Math.random() * 0.8;
        // main tone + a hair-detuned shimmer partner, both long-decaying
        [freq, freq * 1.003].forEach((f, j) => {
          const osc = ctx.createOscillator();
          const cg = makeGain(ctx, vol * (j ? 0.006 : 0.014));
          osc.type = 'sine';
          osc.frequency.value = f;
          cg.gain.setValueAtTime(vol * (j ? 0.006 : 0.014), at);
          cg.gain.exponentialRampToValueAtTime(0.0004, at + decay);
          osc.connect(cg);
          osc.start(at);
          osc.stop(at + decay + 0.05);
        });
      }
    }, 6000);
    return { gain: g, sources: [src, lfo], timer };
  },

  // ── 🔎 Whodunnit CASE BEDS (scenario packs, Nina 07-25: ambience matches the
  //    crime) — picked via scenario.ambient with 'rain' as the classic fallback.
  //    Same contract as every bed: whisper-quiet, sparse events, no assets.

  /** 🗄️ Office at night — fluorescent/fridge hum + a rare distant key clack */
  officehum(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 230; lp.Q.value = 0.5;
    const hum = ctx.createOscillator(); hum.type = 'sine'; hum.frequency.value = 120;
    const humTrim = ctx.createGain(); humTrim.gain.value = 0.18;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.028, ctx.currentTime + 3);
    src.connect(lp); lp.connect(g); hum.connect(humTrim); humTrim.connect(g); g.connect(ctx.destination);
    src.start(); hum.start();
    const timer = setInterval(() => {
      if (Math.random() > 0.22) return; // a clack every ~18s
      const t = ctx.currentTime;
      [0, 0.11].forEach((dt) => {
        const osc = ctx.createOscillator();
        const cg = makeGain(ctx, vol * 0.01);
        osc.type = 'square';
        osc.frequency.value = 1500 + Math.random() * 500;
        cg.gain.setValueAtTime(vol * 0.01, t + dt);
        cg.gain.exponentialRampToValueAtTime(0.001, t + dt + 0.03);
        osc.connect(cg);
        osc.start(t + dt);
        osc.stop(t + dt + 0.03);
      });
    }, 4000);
    return { gain: g, sources: [src, hum], timer };
  },

  /** 🗿 Night museum — dead still air + slow breath + a rare wooden creak */
  museum(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 170; lp.Q.value = 0.4;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.05;
    lfoGain.gain.value = 60;
    lfo.connect(lfoGain); lfoGain.connect(lp.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.03, ctx.currentTime + 3.5);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(); lfo.start();
    const timer = setInterval(() => {
      if (Math.random() > 0.18) return; // a creak every ~22s
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const cg = makeGain(ctx, vol * 0.016);
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(95 + Math.random() * 25, t);
      osc.frequency.exponentialRampToValueAtTime(70, t + 0.5);
      cg.gain.setValueAtTime(0.0001, t);
      cg.gain.linearRampToValueAtTime(vol * 0.016, t + 0.12);
      cg.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
      osc.connect(cg);
      osc.start(t);
      osc.stop(t + 0.6);
    }, 4000);
    return { gain: g, sources: [src, lfo], timer };
  },

  /** 🥂 Reception next door — muffled crowd + glass clinks + a distant beat */
  reception(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 400; lp.Q.value = 0.5;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.036, ctx.currentTime + 3);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start();
    const timer = setInterval(() => {
      const roll = Math.random();
      const t = ctx.currentTime;
      if (roll < 0.25) {
        // glass clink, borrowed rhythm from the café
        [2100, 2700].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const cg = makeGain(ctx, vol * 0.014);
          osc.type = 'sine';
          osc.frequency.value = freq + Math.random() * 150;
          cg.gain.setValueAtTime(vol * 0.014, t + i * 0.04);
          cg.gain.exponentialRampToValueAtTime(0.001, t + i * 0.04 + 0.1);
          osc.connect(cg);
          osc.start(t + i * 0.04);
          osc.stop(t + i * 0.04 + 0.1);
        });
      } else if (roll < 0.4) {
        // three muffled kick thumps through the wall
        [0, 0.42, 0.84].forEach((dt) => {
          const osc = ctx.createOscillator();
          const cg = makeGain(ctx, vol * 0.02);
          osc.type = 'sine';
          osc.frequency.setValueAtTime(72, t + dt);
          osc.frequency.exponentialRampToValueAtTime(48, t + dt + 0.12);
          cg.gain.setValueAtTime(vol * 0.02, t + dt);
          cg.gain.exponentialRampToValueAtTime(0.001, t + dt + 0.14);
          osc.connect(cg);
          osc.start(t + dt);
          osc.stop(t + dt + 0.15);
        });
      }
    }, 4500);
    return { gain: g, sources: [src], timer };
  },

  /** 🧊 Walk-in freezer — deep compressor drone + a rare ice crack */
  freezer(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 140; lp.Q.value = 0.6;
    const drone = ctx.createOscillator(); drone.type = 'sine'; drone.frequency.value = 55;
    const droneTrim = ctx.createGain(); droneTrim.gain.value = 0.3;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.09;
    lfoGain.gain.value = 0.1;
    lfo.connect(lfoGain); lfoGain.connect(droneTrim.gain);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.03, ctx.currentTime + 3.5);
    src.connect(lp); lp.connect(g); drone.connect(droneTrim); droneTrim.connect(g); g.connect(ctx.destination);
    src.start(); drone.start(); lfo.start();
    const timer = setInterval(() => {
      if (Math.random() > 0.15) return; // a crack every ~27s
      const t = ctx.currentTime;
      const burst = makeLoopingNoise(ctx, 0.3);
      const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 2000;
      const cg = makeGain(ctx, vol * 0.012);
      cg.gain.setValueAtTime(vol * 0.012, t);
      cg.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
      burst.connect(hp); hp.connect(cg);
      burst.start(t);
      burst.stop(t + 0.15);
    }, 4000);
    return { gain: g, sources: [src, drone, lfo], timer };
  },

  /** 🦗 Suburban night garden — low wind + cricket trills */
  night(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 280; lp.Q.value = 0.4;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.05;
    lfoGain.gain.value = 130;
    lfo.connect(lfoGain); lfoGain.connect(lp.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.032, ctx.currentTime + 3);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(); lfo.start();
    const timer = setInterval(() => {
      if (Math.random() > 0.45) return; // a trill every ~7s — crickets keep time
      const t = ctx.currentTime;
      const base = 4100 + Math.random() * 400;
      [0, 0.07, 0.14].forEach((dt) => {
        const osc = ctx.createOscillator();
        const cg = makeGain(ctx, vol * 0.007);
        osc.type = 'sine';
        osc.frequency.value = base;
        cg.gain.setValueAtTime(vol * 0.007, t + dt);
        cg.gain.exponentialRampToValueAtTime(0.001, t + dt + 0.05);
        osc.connect(cg);
        osc.start(t + dt);
        osc.stop(t + dt + 0.05);
      });
    }, 3000);
    return { gain: g, sources: [src, lfo], timer };
  },

  /** 🐟 Office aquarium — watery wobble + rising bubble blips */
  aquarium(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 380; lp.Q.value = 0.7;
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.16;
    lfoGain.gain.value = 160;
    lfo.connect(lfoGain); lfoGain.connect(lp.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.03, ctx.currentTime + 3);
    src.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(); lfo.start();
    const timer = setInterval(() => {
      if (Math.random() > 0.4) return; // a bubble run every ~8s
      const t = ctx.currentTime;
      const count = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) {
        const at = t + i * (0.12 + Math.random() * 0.1);
        const osc = ctx.createOscillator();
        const cg = makeGain(ctx, vol * 0.011);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(280 + Math.random() * 120, at);
        osc.frequency.exponentialRampToValueAtTime(750 + Math.random() * 200, at + 0.09);
        cg.gain.setValueAtTime(vol * 0.011, at);
        cg.gain.exponentialRampToValueAtTime(0.001, at + 0.1);
        osc.connect(cg);
        osc.start(at);
        osc.stop(at + 0.11);
      }
    }, 3200);
    return { gain: g, sources: [src, lfo], timer };
  },
};

// ── 🎭 SCENE BEDS (SCENE_SYSTEM_SPEC_2026_08_07 §4.1) ────────────────────────
// A /scene may run ONE short bed for the opening of its life. These are NOT
// ambient beds and they live in their own slot on the manager (`_sceneBed`), so
// a scene can never stop a whodunnit case bed and stopAmbient can never stop a
// scene. Same builder contract as AMBIENT_BEDS: { gain, sources, timer? }.
//
// ⚠️⚠️ SOUND-DESIGN RULE, NON-NEGOTIABLE (spec §4.1b). Nina killed the old
// `airy` bed on 08-02 — three detuned sines at 220/220.25/330.2, i.e. "that
// specific humming/buzzing alien sound" — and clarified on 08-07 that the ban
// is on THAT TIMBRE, not on ambient audio as a category. So: every scene bed is
// filtered NOISE plus sparse irregular events (`chimes`/`rain` DNA). No
// sustained oscillator may be audible on its own. An LFO shaping a noise
// filter is fine; an oscillator you can hum along to is the banned sound
// wearing a new name.
//
// Mixed a little hotter than the ambient beds (which are permanent wallpaper
// and sit at vol*0.02-0.045) because a scene bed is temporary and IS the event.
const SCENE_BEDS = {
  /** 🔥 Fire — warm hiss with resinous pops at irregular intervals. The
      campfire. Pops are randomized in time, brightness and level; an even
      pop rate is the difference between a fire and a Geiger counter. */
  fireBed(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 900; lp.Q.value = 0.5;
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 180;
    // Slow breath on the filter so the flame is never static
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 0.13;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 260;
    lfo.connect(lfoGain); lfoGain.connect(lp.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.06, ctx.currentTime + 1.5);
    src.connect(hp); hp.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(); lfo.start();
    const timer = setInterval(() => {
      const t = ctx.currentTime;
      const pops = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < pops; i++) {
        const at = t + Math.random() * 0.75;
        const len = 0.01 + Math.random() * 0.02;
        const buf = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * len)), ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / d.length, 1.6);
        const psrc = ctx.createBufferSource();
        psrc.buffer = buf;
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = 800 + Math.random() * 2400;
        bp.Q.value = 1.1 + Math.random();
        const pg = makeGain(ctx, vol * (0.05 + Math.random() * 0.09));
        psrc.connect(bp); bp.connect(pg);
        psrc.start(at);
      }
    }, 850);
    return { gain: g, sources: [src, lfo], timer };
  },

  /** 🌬️ Wind — noise through a bandpass that swells and falls in gusts */
  windBed(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 480; bp.Q.value = 0.5;
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 0.09;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 340;
    lfo.connect(lfoGain); lfoGain.connect(bp.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.05, ctx.currentTime + 1.5);
    // Amplitude gusts, slower than the filter sweep so they never lock into a beat
    const ampLfo = ctx.createOscillator(); ampLfo.type = 'sine'; ampLfo.frequency.value = 0.055;
    const ampLfoGain = ctx.createGain(); ampLfoGain.gain.value = vol * 0.022;
    ampLfo.connect(ampLfoGain); ampLfoGain.connect(g.gain);
    src.connect(bp); bp.connect(g); g.connect(ctx.destination);
    src.start(); lfo.start(); ampLfo.start();
    return { gain: g, sources: [src, lfo, ampLfo] };
  },

  /** 🗣️ Crowd — a room full of people, none of them intelligible */
  crowdBed(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 500; bp.Q.value = 0.7;
    // Syllabic wobble: the thing that makes noise read as voices
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = 4.6;
    const lfoGain = ctx.createGain(); lfoGain.gain.value = 150;
    lfo.connect(lfoGain); lfoGain.connect(bp.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.045, ctx.currentTime + 1.5);
    src.connect(bp); bp.connect(g); g.connect(ctx.destination);
    src.start(); lfo.start();
    const timer = setInterval(() => {
      if (Math.random() > 0.35) return; // a laugh-shaped swell every ~9s
      const t = ctx.currentTime;
      const burst = makeLoopingNoise(ctx, 0.6);
      const bbp = ctx.createBiquadFilter();
      bbp.type = 'bandpass'; bbp.frequency.value = 700 + Math.random() * 300; bbp.Q.value = 1.2;
      const bg = makeGain(ctx, 0.0001);
      bg.gain.setValueAtTime(0.0001, t);
      bg.gain.linearRampToValueAtTime(vol * 0.03, t + 0.12);
      bg.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
      burst.connect(bbp); bbp.connect(bg);
      burst.start(t); burst.stop(t + 0.65);
    }, 3000);
    return { gain: g, sources: [src, lfo], timer };
  },

  /** 🌊 Ocean — the swell IS the sound: a slow gain LFO, not a filter one */
  oceanBed(ctx, vol) {
    const src = makeLoopingNoise(ctx);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 700; lp.Q.value = 0.4;
    const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 120;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.linearRampToValueAtTime(vol * 0.05, ctx.currentTime + 1.5);
    // ~11s wave period — slow enough to feel like water, not tremolo
    const swell = ctx.createOscillator(); swell.type = 'sine'; swell.frequency.value = 0.09;
    const swellGain = ctx.createGain(); swellGain.gain.value = vol * 0.03;
    swell.connect(swellGain); swellGain.connect(g.gain);
    // Brightness follows the swell so crests hiss and troughs go dull
    const bright = ctx.createGain(); bright.gain.value = 420;
    swell.connect(bright); bright.connect(lp.frequency);
    src.connect(hp); hp.connect(lp); lp.connect(g); g.connect(ctx.destination);
    src.start(); swell.start();
    return { gain: g, sources: [src, swell] };
  },
};

// rainBed / stormBed are the existing noir beds under scene-bed names — same
// DNA, already tuned, no reason to write them twice.
SCENE_BEDS.rainBed = AMBIENT_BEDS.rain;
SCENE_BEDS.stormBed = AMBIENT_BEDS.storm;

/** Map a space's name/scene text to an ambient bed. Deliberately keyword-dumb:
    wrong-but-plausible beats silent. Default = 'chimes' (Nina 07-24 — the old
    'airy' fallback buzzed; it now needs dreamy keywords to appear at all). */
export function pickAmbientTheme(text) {
  const hay = (text || '').toLowerCase();
  if (/rain|noir|myster|detect|storm|crime|haunt/.test(hay)) return 'rain';
  if (/caf[eé]|coffee|tea |tea-|bakery|diner|cozy|lounge|library|book|study/.test(hay)) return 'cafe';
  if (/forest|garden|nature|jungle|island|beach|camp|mountain|woods|meadow|farm/.test(hay)) return 'forest';
  if (/arcade|retro|cyber|neon|pixel|robot|lab\b/.test(hay)) return 'arcade';
  if (/dream|sky|cloud|cosmic|celestial|starlit|zen|spa\b|float/.test(hay)) return 'airy';
  return 'chimes';
}

// Export singleton
const soundManager = new SoundManager();
export default soundManager;
